import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { useSelector } from "react-redux";
import HeroSection from "../Story-Elements/HeroSection";
import NavigationBox from "../Story-Elements/NavigationBox";
import PremiumArticleBody from "./components/PremiumArticleBody";
import AlsoReadSection from "../Story-Elements/AlsoReadSection";
import { MembershipGateBanner } from "../../atoms/membership-gate-banner";
import { isStoryVisibleToUser } from "../../../utils/forum-filter";
import { getStoryData } from "../text-story/stories";
import "../text-story/text-story.m.css";

const PremiumStory = ({
  story,
  config,
  hasAccess,
  subscriptionPlans = [],
  initRazorPayPayment,
  getSubscriptionForUser,
  accessTypeBkIntegrationId,
  member: memberProp,
  hasUserPurchasedStory = false,
  isPPACheckComplete = false,
}) => {
  // Get member and subscriber metadata from Redux store
  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const isAuthInitializing = useSelector((state) => get(state, ["isAuthInitializing"], false));

  // Loading states - start with loading for forum-based stories
  const [isAccessCheckComplete, setIsAccessCheckComplete] = useState(false);
  const [userHasAccess, setUserHasAccess] = useState(true);
  const [isReadyToShow, setIsReadyToShow] = useState(false);

  const {
    desktopSvg,
    entityName,
    sectionName,
    sectionSlug,
    firstPublishedAt,
    quarter,
    timezone,
  } = getStoryData(story);

  // Get forum attribute from story metadata
  const forumAttribute = get(story, ['metadata', 'story-attributes', 'forum'], []);
  const forumAttributes = Array.isArray(forumAttribute) ? forumAttribute : (forumAttribute ? [forumAttribute] : []);

  // Check if this is a forum-based story that needs access control
  const isForumBasedStory = forumAttributes.length > 0;

  // Check if subscriberMetadata is still loading
  // If user is logged in but subscriberMetadata is null, it's still loading
  const isMetadataLoading = member && subscriberMetadata === null;

  // Effect to handle access check when member/metadata changes
  useEffect(() => {
    if (isForumBasedStory) {
      // Always start with loading state
      setIsAccessCheckComplete(false);
      setUserHasAccess(false);

      // Don't check access if auth is initializing or metadata is still loading
      if (isAuthInitializing || isMetadataLoading) {
        return;
      }

      const checkAccess = () => {
        // Use isStoryVisibleToUser to determine access based on member state and subscriberMetadata
        const hasStoryAccess = isStoryVisibleToUser(member, subscriberMetadata, story);
        setUserHasAccess(hasStoryAccess);
        setIsAccessCheckComplete(true);

        // Additional delay before showing the gate to prevent flash
        setTimeout(() => {
          setIsReadyToShow(true);
        }, 200);
      };

      // Delay to ensure smooth transition and prevent flash
      const timeoutId = setTimeout(checkAccess, 300);
      return () => clearTimeout(timeoutId);
    } else {
      // For non-forum stories, set access immediately
      setIsAccessCheckComplete(true);
      setUserHasAccess(true);
    }
  }, [member, subscriberMetadata, story, isForumBasedStory, isMetadataLoading, isAuthInitializing]);

  // Check if story requires membership and user doesn't have access
  const showMembershipGate = isAccessCheckComplete && !userHasAccess && isForumBasedStory && isReadyToShow;

  // Hide back to top button when membership gate is shown
  useEffect(() => {
    const backToTopBtn = document.getElementById('backToTopBtn');
    if (showMembershipGate && backToTopBtn) {
      backToTopBtn.style.display = 'none';
    }

    // Show back to top button when membership gate is hidden
    return () => {
      if (backToTopBtn) {
        backToTopBtn.style.display = '';
      }
    };
  }, [showMembershipGate]);

  // Determine the forum type for the banner
  const getForumType = () => {
    if (forumAttributes.length > 0 && !forumAttributes.includes('All-forums')) {
      return forumAttributes.join(', '); // Join all forum attributes with comma
    }
    return "CEO"; // Default
  };

  return (
    <div styleName="text-story premium-article" className="article-print-wrapper article-printable">
      <div styleName="hero-nav-container">
        <HeroSection
          story={story}
          desktopSvg={desktopSvg}
          sectionName={sectionName}
          sectionSlug={sectionSlug}
          entityName={entityName}
          quarter={quarter}
          firstPublishedAt={firstPublishedAt}
          config={config}
          disableListenPrint={!hasUserPurchasedStory}
        />

        <NavigationBox story={story} />
      </div>

      <div styleName="content-wrapper">
        <div styleName="article-container" className="article-container">
          <PremiumArticleBody
            story={story}
            hasAccess={hasAccess}
            timezone={timezone}
            subscriptionPlans={subscriptionPlans}
            initRazorPayPayment={initRazorPayPayment}
            getSubscriptionForUser={getSubscriptionForUser}
            accessTypeBkIntegrationId={accessTypeBkIntegrationId}
            member={memberProp || member}
            hasUserPurchasedStory={hasUserPurchasedStory}
            isPPACheckComplete={isPPACheckComplete}
          />
        </div>
      </div>

      <AlsoReadSection storyId={story.id} />

      {/* Show membership gate banner when user doesn't have access */}
      {isForumBasedStory && showMembershipGate && (
        <MembershipGateBanner
          isVisible={true}
          reportTitle={story.headline || "This report"}
          forumType={getForumType()}
          exploreLink="/collection/research-hub"
          isLoggedIn={!!member}
          userCurrentForum={get(subscriberMetadata, 'primary-role') || get(subscriberMetadata, 'secondary-role')}
        />
      )}
    </div>
  );
};

PremiumStory.propTypes = {
  story: PropTypes.object.isRequired,
  config: PropTypes.object,
  hasAccess: PropTypes.bool,
  subscriptionPlans: PropTypes.array,
  initRazorPayPayment: PropTypes.func,
  getSubscriptionForUser: PropTypes.func,
  accessTypeBkIntegrationId: PropTypes.string,
  member: PropTypes.object,
  hasUserPurchasedStory: PropTypes.bool,
  isPPACheckComplete: PropTypes.bool,
};

export default PremiumStory;
